import { Copy, FolderArchive, Pencil, Trash2 } from 'lucide-react-native';
import { useState } from 'react';
import { Platform, Pressable, StyleSheet, View, type ViewStyle } from 'react-native';
import { Badge } from '../../../components/primitives/Badge';
import { IconButton } from '../../../components/primitives/IconButton';
import { HStack, VStack } from '../../../components/primitives/Stack';
import { Text } from '../../../components/primitives/Text';
import { useTranslation } from '../../../contexts/TranslationContext';
import { colors } from '../../../theme/colors';
import { tokens } from '../../../theme/tokens';
import type { VoiceAgent, VoiceAgentStatus } from '../types';

type BadgeColor = 'cyan' | 'green' | 'red' | 'amber' | 'purple' | 'blue' | 'neutral';

const STATUS_BADGE_COLORS: Record<VoiceAgentStatus, BadgeColor> = {
  draft: 'amber',
  published: 'green',
  archived: 'neutral',
};

interface AgentCardProps {
  agent: VoiceAgent;
  onPress: (agent: VoiceAgent) => void;
  onEdit?: (agent: VoiceAgent) => void;
  onDuplicate: (agent: VoiceAgent) => void;
  onArchive: (agent: VoiceAgent) => void;
  onDelete: (agent: VoiceAgent) => void;
}

export function AgentCard({
  agent,
  onPress,
  onEdit,
  onDuplicate,
  onArchive,
  onDelete,
}: Readonly<AgentCardProps>) {
  const { t } = useTranslation();
  const [hovered, setHovered] = useState(false);

  const statusLabel = t(`voiceAgents.card.status.${agent.status}`);
  const useCaseLabel = t(`voiceAgents.card.useCase.${agent.useCase}`);
  const useCaseDisplay =
    useCaseLabel === `voiceAgents.card.useCase.${agent.useCase}` ? agent.useCase : useCaseLabel;
  const languageLabel = t(`voiceAgents.card.language.${agent.language}`);
  const languageDisplay =
    languageLabel === `voiceAgents.card.language.${agent.language}`
      ? agent.language
      : languageLabel;

  const hoverProps = Platform.select({
    web: {
      onMouseEnter: () => setHovered(true),
      onMouseLeave: () => setHovered(false),
    },
    default: {},
  });

  const webHoverStyle = Platform.select<ViewStyle>({
    web: {
      transition: 'border-color 0.15s ease, background-color 0.15s ease',
      cursor: 'pointer',
    } as ViewStyle,
    default: {},
  });

  const initial = agent.name[0]?.toUpperCase() ?? '?';

  return (
    <Pressable
      onPress={() => onPress(agent)}
      accessible
      accessibilityRole="button"
      accessibilityLabel={`${agent.name} — ${statusLabel} ${t('voiceAgents.card.voiceAgent')}`}
      style={styles.pressable}
    >
      <View style={[styles.card, hovered && styles.cardHover, webHoverStyle]} {...hoverProps}>
        <HStack spacing="md" align="start" style={styles.mainRow}>
          <View style={styles.avatar}>
            <Text style={styles.avatarLetter}>{initial}</Text>
          </View>

          <VStack spacing="xs" style={styles.body}>
            <View style={styles.header}>
              <Text
                variant="body"
                color={colors.text.primary}
                style={styles.name}
                numberOfLines={1}
                ellipsizeMode="tail"
                accessibilityRole="header"
              >
                {agent.name}
              </Text>
              <Badge
                color={STATUS_BADGE_COLORS[agent.status]}
                size="sm"
                accessibilityLabel={`${t('voiceAgents.card.statusPrefix')}: ${statusLabel}`}
              >
                {statusLabel}
              </Badge>
            </View>

            <Text
              variant="caption"
              color={colors.text.secondary}
              style={styles.description}
              numberOfLines={2}
              accessibilityElementsHidden={!agent.description}
              importantForAccessibility={agent.description ? 'auto' : 'no-hide-descendants'}
            >
              {agent.description || '\u00A0'}
            </Text>

            <HStack spacing="xs" align="center" style={styles.meta}>
              <Text variant="caption" color={colors.text.tertiary} style={styles.metaText}>
                {useCaseDisplay}
              </Text>
              <View style={styles.dot} />
              <Text variant="caption" color={colors.text.tertiary} style={styles.metaText}>
                {languageDisplay}
              </Text>
              {agent.version > 0 ? (
                <>
                  <View style={styles.dot} />
                  <Text
                    variant="caption"
                    color={colors.text.tertiary}
                    style={styles.metaText}
                    accessibilityLabel={`${t('voiceAgents.card.version')} ${agent.version}`}
                  >
                    v{agent.version}
                  </Text>
                </>
              ) : null}
            </HStack>

            <Text
              variant="caption"
              color={colors.text.disabled}
              style={styles.publishedAt}
              accessibilityElementsHidden={!agent.publishedAt}
              importantForAccessibility={agent.publishedAt ? 'auto' : 'no-hide-descendants'}
            >
              {agent.publishedAt ? (
                <>
                  {t('voiceAgents.card.publishedOn')}{' '}
                  {new Date(agent.publishedAt).toLocaleDateString()}
                </>
              ) : (
                '\u00A0'
              )}
            </Text>
          </VStack>
        </HStack>

        <HStack spacing="xs" align="center" style={styles.actions}>
          {onEdit ? (
            <IconButton
              icon={<Pencil size={16} color={colors.primary.neon} strokeWidth={1.8} />}
              size="sm"
              variant="outline"
              accessibilityLabel={`${t('voiceAgents.card.edit')} ${agent.name}`}
              tooltip={t('voiceAgents.card.tooltipEdit')}
              onPress={(event) => {
                event?.stopPropagation?.();
                onEdit(agent);
              }}
              hitSlop={8}
              style={styles.actionBtn}
            />
          ) : null}
          <IconButton
            icon={<Copy size={16} color={colors.text.secondary} strokeWidth={1.8} />}
            size="sm"
            variant="outline"
            accessibilityLabel={`${t('voiceAgents.card.duplicate')} ${agent.name}`}
            tooltip={t('voiceAgents.card.tooltipDuplicate')}
            onPress={(e) => {
              e.stopPropagation?.();
              onDuplicate(agent);
            }}
            hitSlop={8}
            style={styles.actionBtn}
          />

          {agent.status !== 'archived' ? (
            <IconButton
              icon={<FolderArchive size={16} color={colors.text.secondary} strokeWidth={1.8} />}
              size="sm"
              variant="outline"
              accessibilityLabel={`${t('voiceAgents.card.archive')} ${agent.name}`}
              tooltip={t('voiceAgents.card.tooltipArchive')}
              onPress={(e) => {
                e.stopPropagation?.();
                onArchive(agent);
              }}
              hitSlop={8}
              style={styles.actionBtn}
            />
          ) : null}

          <IconButton
            icon={<Trash2 size={16} color={colors.status.error.main} strokeWidth={1.8} />}
            size="sm"
            variant="outline"
            accessibilityLabel={`${t('voiceAgents.card.delete')} ${agent.name}`}
            tooltip={t('voiceAgents.card.tooltipDelete')}
            onPress={(e) => {
              e.stopPropagation?.();
              onDelete(agent);
            }}
            hitSlop={8}
            style={styles.actionBtn}
          />
        </HStack>
      </View>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  pressable: {
    flex: 1,
    minHeight: 204,
    alignSelf: 'stretch',
    ...(Platform.OS === 'web' ? ({ width: '100%' } as ViewStyle) : {}),
  },
  card: {
    flex: 1,
    backgroundColor: colors.surface.row,
    borderRadius: tokens.radius.lg,
    borderWidth: 1,
    borderColor: colors.border.muted,
    padding: tokens.spacing.md,
    gap: tokens.spacing.md,
    // NOTE: no `overflow: 'hidden'` here — it would clip the action-icon hover
    // tooltips (which are absolutely positioned and extend past the card edge).
    // The rounded corners still clip the background fill natively via borderRadius.
  },
  cardHover: {
    backgroundColor: colors.surface.rowHover,
    borderColor: colors.primary.borderActive,
  },
  mainRow: {
    flex: 1,
    minWidth: 0,
  },
  avatar: {
    width: 42,
    height: 42,
    borderRadius: 21,
    backgroundColor: colors.primary.muted,
    alignItems: 'center',
    justifyContent: 'center',
  },
  avatarLetter: {
    color: colors.primary.neon,
    fontWeight: '700',
    fontSize: 16,
    lineHeight: 18,
  },
  body: {
    flex: 1,
    minWidth: 0,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: tokens.spacing.sm,
  },
  name: {
    flexShrink: 1,
    minWidth: 0,
    fontWeight: '700',
    fontSize: 15,
  },
  description: {
    minHeight: 32,
    fontSize: 12,
    lineHeight: 16,
  },
  meta: {
    minHeight: 16,
    flexWrap: 'wrap',
    marginTop: 2,
  },
  metaText: {
    fontSize: 11,
    marginLeft: Platform.OS === 'web' ? 0 : 3,
  },
  dot: {
    width: 3,
    height: 3,
    borderRadius: 1.5,
    backgroundColor: colors.text.disabled,
  },
  publishedAt: {
    minHeight: 16,
    marginTop: 2,
    fontSize: 11,
  },
  actions: {
    alignSelf: 'flex-end',
    paddingTop: tokens.spacing.sm,
    borderTopWidth: 1,
    borderTopColor: colors.border.subtle,
    width: '100%',
    justifyContent: 'flex-end',
  },
  actionBtn: {
    width: 36,
    height: 36,
  },
});
